/**
 * PAPPO — ContactsPage
 * Consciente do papel:
 *  - Criança: sem geração de link. As amizades do filho são conectadas pelos
 *    responsáveis ("Conectar nossos filhos" — ver ChildFriendships).
 *  - Adulto: "Adicionar" direto por username + "Convidar amigo" (auto-conexão),
 *    e o painel de amizades dos filhos.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Search, Check, XCircle, Link2, Copy } from 'lucide-react';
import type { Contact } from '../types/user';
import {
  listContacts,
  requestContact,
  acceptContact,
  removeContact,
  setAlias,
  archiveWith,
  unarchiveWith,
} from '../services/contacts';
import { createConversation } from '../services/conversations';
import { createInvite } from '../services/invitations';
import { useAuth } from '../hooks/useAuth';
import { ContactList } from '../components/contacts/ContactList';
import { blockUser, unblockUser } from '../services/blocks';
import { AddContact } from '../components/contacts/AddContact';
import { ReportModal } from '../components/contacts/ReportModal';
import { ChildFriendships } from '../components/contacts/ChildFriendships';
import { CoGuardians } from '../components/contacts/CoGuardians';
import { Avatar } from '../components/common/Avatar';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import styles from './ContactsPage.module.css';

export function ContactsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isChild = user?.role === 'child';
  const isParent = user?.role === 'parent' || user?.role === 'admin';

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [pending, setPending] = useState<Contact[]>([]);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  /* Alvo da denúncia aberta no modal (só responsáveis) */
  const [reportTarget, setReportTarget] = useState<{ id: number; name: string } | null>(null);

  const load = useCallback(async () => {
    const res = await listContacts();
    if (res.success && res.data) {
      setContacts(res.data.contacts);
      setPending(res.data.pending_incoming);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleInvite = useCallback(async () => {
    const res = await createInvite();
    if (res.success && res.data) {
      setInviteLink(`${window.location.origin}/invite/${res.data.token}`);
      setFeedback('');
    }
  }, []);

  const handleCopy = useCallback(() => {
    if (inviteLink) {
      navigator.clipboard?.writeText(inviteLink);
      setFeedback('Link copiado!');
    }
  }, [inviteLink]);

  const handleAdd = useCallback(
    async (username: string) => {
      const res = await requestContact(username);
      if (res.success) {
        setFeedback(
          res.data?.pending_guardian_approval
            ? 'Pedido enviado — aguarda aprovação do responsável do menor.'
            : res.data?.accepted
            ? 'Contacto aceite!'
            : 'Pedido enviado!'
        );
        await load();
        return { success: true };
      }
      return { success: false, error: res.error };
    },
    [load]
  );

  const handleAccept = useCallback(
    async (id: number) => {
      await acceptContact(id);
      await load();
    },
    [load]
  );

  const handleReject = useCallback(
    async (id: number) => {
      await removeContact(id);
      await load();
    },
    [load]
  );

  const handleStartChat = useCallback(
    async (contact: Contact) => {
      if (contact.status !== 'accepted' || !contact.contact_user) return;
      const res = await createConversation(contact.contact_user.id);
      if (res.success && res.data) {
        navigate('/chat', {
          state: { openConversationId: res.data.conversation.id },
        });
      }
    },
    [navigate]
  );

  /* ⋮ Renomear — define/atualiza o apelido local (vazio remove) */
  const handleRename = useCallback(
    async (contact: Contact, alias: string) => {
      const targetId = contact.contact_user?.id;
      if (!targetId) return;
      const res = await setAlias(targetId, alias);
      if (res.success) {
        setFeedback(alias ? 'Apelido guardado.' : 'Apelido removido.');
        await load();
      }
    },
    [load]
  );

  /* ⋮ Arquivar/Desarquivar — só visual, per-user (não afeta supervisão) */
  const handleArchiveToggle = useCallback(
    async (contact: Contact, archived: boolean) => {
      const targetId = contact.contact_user?.id;
      if (!targetId) return;
      const res = archived
        ? await archiveWith(targetId)
        : await unarchiveWith(targetId);
      if (res.success) {
        setFeedback(archived ? 'Conversa arquivada.' : 'Conversa desarquivada.');
        await load();
      }
    },
    [load]
  );

  /* ⋮ Bloquear — efeito 1:1 bidirecional; a trava de família é recusada no backend */
  const handleBlock = useCallback(
    async (_contact: Contact, targetId: number) => {
      if (!targetId) return;
      const res = await blockUser(targetId);
      if (res.success) {
        setFeedback('Contacto bloqueado.');
        await load();
      } else {
        setFeedback(res.error || 'Não foi possível bloquear este contacto.');
      }
    },
    [load]
  );

  /* ⋮ Desbloquear — remove o bloqueio 1:1 e recarrega a lista */
  const handleUnblock = useCallback(
    async (_contact: Contact, targetId: number) => {
      if (!targetId) return;
      const res = await unblockUser(targetId);
      if (res.success) {
        setFeedback('Contacto desbloqueado.');
        await load();
      } else {
        setFeedback(res.error || 'Não foi possível desbloquear este contacto.');
      }
    },
    [load]
  );

  /* ⋮ Denunciar — abre o modal de denúncia (só responsáveis; alvos não-família) */
  const handleReport = useCallback((contact: Contact, targetId: number) => {
    if (!targetId) return;
    setReportTarget({ id: targetId, name: contact.contact_user?.display_name || 'utilizador' });
  }, []);

  const filteredContacts = contacts.filter((c) => {
    const q = search.toLowerCase();
    const name = (c.alias || c.contact_user?.display_name || '').toLowerCase();
    return name.includes(q);
  });

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>Contactos</h2>
          <div className={styles.pendingActions}>
            {!isChild && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Link2 size={16} />}
                  onClick={handleInvite}
                >
                  Convidar amigo
                </Button>
                <Button
                  size="sm"
                  leftIcon={<UserPlus size={16} />}
                  onClick={() => setShowAddModal(true)}
                >
                  Adicionar
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Link de convite gerado */}
        {inviteLink && (
          <div className={styles.feedback}>
            <p style={{ margin: '0 0 6px' }}>
              Envie este link. Quando a pessoa criar a conta, vocês já ficam
              conectados:
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{ wordBreak: 'break-all', flex: 1 }}>{inviteLink}</code>
              <Button size="sm" variant="ghost" leftIcon={<Copy size={14} />} onClick={handleCopy}>
                Copiar
              </Button>
            </div>
          </div>
        )}

        {/* Amizades dos filhos (mediadas pelos pais) — só parent/admin */}
        {isParent && (
          <ChildFriendships
            adultContacts={contacts.filter((c) => c.status === 'accepted')}
          />
        )}

        {/* Co-responsáveis (cônjuge / guarda compartilhada) — só parent/admin */}
        {isParent && <CoGuardians />}

        {/* Pedidos recebidos (adultos) */}
        {pending.length > 0 && (
          <div className={styles.pending}>
            <p className={styles.pendingTitle}>
              Pedidos recebidos ({pending.length})
            </p>
            {pending.map((p) => (
              <div key={p.id} className={styles.pendingItem}>
                <Avatar
                  name={p.contact_user?.display_name ?? 'Utilizador'}
                  src={p.contact_user?.avatar_url}
                  size="sm"
                />
                <span className={styles.pendingName}>
                  {p.contact_user?.display_name ?? 'Utilizador'}
                </span>
                <div className={styles.pendingActions}>
                  <Button size="sm" leftIcon={<Check size={14} />} onClick={() => handleAccept(p.id)}>
                    Aceitar
                  </Button>
                  <Button size="sm" variant="ghost" leftIcon={<XCircle size={14} />} onClick={() => handleReject(p.id)}>
                    Recusar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Search */}
        <div className={styles.search}>
          <Input
            placeholder="Pesquisar contactos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search size={16} />}
            inputSize="sm"
          />
        </div>

        {feedback && <div className={styles.feedback}>{feedback}</div>}

        {/* List */}
        <div className={styles.listWrapper}>
          {isLoading ? (
            <div className={styles.feedback}>A carregar contactos...</div>
          ) : (
            <ContactList
              contacts={filteredContacts}
              onSelect={handleStartChat}
              onRename={handleRename}
              onArchiveToggle={handleArchiveToggle}
              onBlock={handleBlock}
              onUnblock={handleUnblock}
              onReport={isParent ? handleReport : undefined}
            />
          )}
        </div>
      </div>

      {/* Add Contact Modal (apenas adultos) */}
      {!isChild && (
        <AddContact
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAdd}
        />
      )}

      {/* Denúncia (só responsáveis) */}
      {reportTarget && (
        <ReportModal
          targetId={reportTarget.id}
          targetName={reportTarget.name}
          onClose={() => setReportTarget(null)}
          onDone={() => setFeedback('Denúncia enviada. A equipe de moderação vai analisar.')}
        />
      )}
    </div>
  );
}
